import React from 'react';
import {
  Box,
  Grid,
  Chip,
  Typography,
  Divider
} from '@mui/material';
import {
  DriveFileRenameOutlineOutlined,
  TableChartOutlined,
  BoltOutlined,
  AutoFixHighOutlined,
  PowerSettingsNewOutlined,
  ScheduleOutlined,
  PlayArrowOutlined,
  FilterAltOutlined,
  FlashOnOutlined,
  AssessmentOutlined
} from '@mui/icons-material';
import { useAutomationContext } from '../context/AutomationContext';
import { useAutomationMetadata } from '../hooks/useAutomationMetadata';
import { SectionCard, SummaryField, EmptyState } from '../components/StepSection';
import { badgeSx, statusChipSx, statusColors, tintSx, FONT } from '../styles/wizardSx';

function CountChip({ count }) {
  if (!count) return null;
  return (
    <Chip
      label={count}
      size="small"
      sx={{ bgcolor: 'primary.main', color: 'primary.contrastText', fontWeight: 700, fontSize: FONT.caption, height: '22px' }}
    />
  );
}

function ReviewStep() {
  const { state } = useAutomationContext();
  const { metadata } = useAutomationMetadata();

  const objectMetadata = metadata?.objects?.[state.wizardData.object];
  const EVENT_PHRASE = {
    create: 'created',
    update: 'updated',
    delete: 'deleted',
  };
  const rawTrigger = (state.wizardData.trigger || '')
    .replace(/^on[_\s]+/i, '')
    .trim()
    .toLowerCase();
  const eventPhrase = EVENT_PHRASE[rawTrigger] || rawTrigger;
  const objectLabel = (
    objectMetadata?.name ||
    state.wizardData.object ||
    'the selected object'
  )
    .toString()
    .replace(/_/g, ' ')
    .toLowerCase();
  const triggerDescription = state.wizardData.trigger
    ? `when ${objectLabel} is ${eventPhrase}`
    : 'when this automation is triggered';
  const actionCount = state.actions.length;
  const conditionCount = state.conditionGroups.reduce(
    (sum, group) => sum + (group.conditions?.length || 0),
    0
  );
  const humanSummary = `This automation will run ${triggerDescription}. It will evaluate ${conditionCount || 'no'} condition${conditionCount === 1 ? '' : 's'} and perform ${actionCount || 'no'} action${actionCount === 1 ? '' : 's'}.`;
  const totalOperations = conditionCount + actionCount;

  return (
    <Grid container spacing={3}>
      {/* Summary */}
      <Grid item xs={12}>
        <Box
          sx={[
            {
              p: 2.5,
              borderRadius: '12px',
              border: '1px solid',
              borderColor: 'outline.variant',
              display: 'flex',
              alignItems: 'center',
              gap: 2.5,
              flexWrap: 'wrap'
            },
            (theme) => tintSx(theme, 0.06)
          ]}
        >
          <Box sx={badgeSx(40)}>
            <AssessmentOutlined />
          </Box>
          <Box sx={{ flex: 1, minWidth: 240 }}>
            <Typography sx={{ fontSize: FONT.title, fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
              Automation Summary
            </Typography>
            <Typography sx={{ fontSize: FONT.body, color: 'text.primary', lineHeight: 1.6 }}>
              {humanSummary}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box sx={{ textAlign: 'right' }}>
              <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                Total operations
              </Typography>
              <Typography sx={{ fontSize: '24px', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}>
                {totalOperations}
              </Typography>
            </Box>
            <Chip
              icon={<PowerSettingsNewOutlined sx={{ fontSize: 16 }} />}
              label={state.wizardData.isActive ? 'Will be activated' : 'Will remain inactive'}
              sx={statusChipSx(state.wizardData.isActive ? 'success' : 'neutral')}
            />
          </Box>
        </Box>
      </Grid>

      {/* Basic Information */}
      <Grid item xs={12} md={6}>
        <SectionCard
          icon={<DriveFileRenameOutlineOutlined fontSize="small" />}
          title="Basic Information"
          sx={{ height: '100%' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SummaryField label="Automation name">
              <Typography sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                {state.wizardData.name || 'Not specified'}
              </Typography>
            </SummaryField>
            <SummaryField label="Description">
              <Typography sx={{ fontSize: FONT.body, color: state.wizardData.description ? 'text.primary' : 'text.secondary' }}>
                {state.wizardData.description || 'No description provided'}
              </Typography>
            </SummaryField>
            <Divider sx={{ my: 0 }} />
            <SummaryField label="Status">
              <Chip
                icon={<PowerSettingsNewOutlined sx={{ fontSize: 16 }} />}
                label={state.wizardData.isActive ? 'Active' : 'Inactive'}
                size="small"
                sx={statusChipSx(state.wizardData.isActive ? 'success' : 'neutral')}
              />
            </SummaryField>
          </Box>
        </SectionCard>
      </Grid>

      {/* Trigger */}
      <Grid item xs={12} md={6}>
        <SectionCard
          icon={<BoltOutlined fontSize="small" />}
          title="Trigger Configuration"
          sx={{ height: '100%' }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SummaryField label="Object">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TableChartOutlined fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                  {objectMetadata?.name || state.wizardData.object}
                </Typography>
                {objectMetadata?.type === 'custom' && (
                  <Chip label="Custom" size="small" sx={[{ height: '22px' }, statusChipSx('info')]} />
                )}
              </Box>
            </SummaryField>
            <SummaryField label="Trigger event">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <FlashOnOutlined fontSize="small" sx={{ color: 'primary.main' }} />
                <Typography sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                  {state.wizardData.trigger?.charAt(0).toUpperCase() + state.wizardData.trigger?.slice(1).replace('_', ' ')}
                </Typography>
              </Box>
            </SummaryField>
          </Box>
        </SectionCard>
      </Grid>

      {/* Conditions */}
      <Grid item xs={12} md={6}>
        <SectionCard
          icon={<FilterAltOutlined fontSize="small" />}
          title="Conditions"
          action={<CountChip count={state.conditionGroups.length} />}
          sx={{ height: '100%' }}
        >
          {state.conditionGroups.length === 0 ? (
            <EmptyState
              compact
              title="No conditions configured"
              description="Automation will run on every trigger"
            />
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {state.conditionGroups.map((group, index) => (
                <Box component="li" key={group.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={badgeSx(28)}>{index + 1}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography noWrap sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                        {group.name}
                      </Typography>
                      <Chip
                        label={group.logic}
                        size="small"
                        sx={[{ height: '20px' }, statusChipSx(group.logic === 'AND' ? 'success' : 'warning')]}
                      />
                    </Box>
                    <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                      {group.conditions.length} condition{group.conditions.length !== 1 ? 's' : ''}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      </Grid>

      {/* Actions */}
      <Grid item xs={12} md={6}>
        <SectionCard
          icon={<AutoFixHighOutlined fontSize="small" />}
          title="Actions"
          action={<CountChip count={state.actions.length} />}
          sx={{ height: '100%' }}
        >
          {state.actions.length === 0 ? (
            <EmptyState
              compact
              title="No actions configured"
              description="Add actions to define automation behavior"
            />
          ) : (
            <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {state.actions.map((action, index) => (
                <Box component="li" key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box sx={badgeSx(28)}>{index + 1}</Box>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography sx={{ fontSize: FONT.body, fontWeight: 600, color: 'text.primary' }}>
                      {action.type ?
                        action.type.charAt(0).toUpperCase() +
                        action.type.slice(1).replace(/_/g, ' ') :
                        'Unconfigured Action'}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {action.timeDelay?.enabled ? (
                        <>
                          <ScheduleOutlined
                            sx={(theme) => ({ fontSize: 14, color: statusColors(theme, 'warning').text })}
                          />
                          <Typography
                            sx={(theme) => ({ fontSize: FONT.caption, color: statusColors(theme, 'warning').text })}
                          >
                            Delayed: {action.timeDelay.days}d {action.timeDelay.hours}h {action.timeDelay.minutes}m
                          </Typography>
                        </>
                      ) : (
                        <>
                          <PlayArrowOutlined sx={{ fontSize: 14, color: 'text.secondary' }} />
                          <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
                            Immediate
                          </Typography>
                        </>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </SectionCard>
      </Grid>
    </Grid>
  );
}

export default ReviewStep;
